import { z } from 'zod';
import { documentSummarySchema } from './documents';

export const SEARCH_QUERY_MAX = 120;

/**
 * How results are ordered. Newest first is the default; `relevance` needs search words, and
 * `cited` applies to articles only (each kind falls back to newest for an order it lacks).
 */
export const searchSortSchema = z.enum(['newest', 'oldest', 'relevance', 'title', 'cited']);
export type SearchSort = z.infer<typeof searchSortSchema>;

/**
 * What a search looks through: Classics (`book`), open-access articles (`article`),
 * open-access books (`openbook`, OAPEN) or the books readers added (`added`).
 */
export const searchKindSchema = z.enum(['book', 'article', 'openbook', 'added']);
export type SearchKind = z.infer<typeof searchKindSchema>;

export const SORTS_BY_KIND = {
  book: ['newest', 'oldest', 'relevance', 'title'],
  article: ['newest', 'oldest', 'relevance', 'cited'],
  openbook: ['newest', 'oldest', 'relevance', 'title'],
  added: ['newest', 'oldest', 'relevance', 'title'],
} as const satisfies Record<SearchKind, readonly SearchSort[]>;

export const searchQuerySchema = z.object({
  kind: searchKindSchema.default('book'),
  q: z.string().trim().max(SEARCH_QUERY_MAX).default(''),
  sort: searchSortSchema.default('newest'),
  /** Opaque continuation token from the previous page. */
  cursor: z.string().max(500).optional(),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

/** One page of results, English only, in the order asked for. */
export const searchPageSchema = z.object({
  items: z.array(documentSummarySchema),
  total: z.number().int().min(0),
  nextCursor: z.string().nullable(),
});
export type SearchPage = z.infer<typeof searchPageSchema>;

export const alsoFoundQuerySchema = z.object({
  q: z.string().trim().max(SEARCH_QUERY_MAX).default(''),
});

/** An open-access book from OAPEN, as shown before it can be read here. */
export const openBookSchema = documentSummarySchema.extend({
  abstract: z.string().nullable(),
  subjects: z.array(z.string()),
  /** Where the book is read while OAPEN does not let Libellus fetch it. */
  oapenUrl: z.string(),
  readableHere: z.boolean(),
});
export type OpenBook = z.infer<typeof openBookSchema>;

export const openBookParamsSchema = z.object({ handleId: z.coerce.number().int().min(1) });
